import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Line, Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, BarElement, PointElement, Title, Tooltip, Legend, Filler } from "chart.js";
import toast from "react-hot-toast";
import { adminAPI } from "../api";
import api from "../api";
import useAuthStore from "../store/auth";

ChartJS.register(CategoryScale, LinearScale, LineElement, BarElement, PointElement, Title, Tooltip, Legend, Filler);

const PAYMENT_METHODS = {
  paypal:      { label: "PayPal",        icon: "🅿️",  color: "#0070ba" },
  vanilla_pay: { label: "Vanilla Pay",   icon: "📱",  color: "#00c853" },
  card:        { label: "Carte bancaire",icon: "💳",  color: "#7c6af7" },
};

const EVENT_LABELS = {
  "payment.captured": { label: "Paiement",     color: "var(--accent3)", bg: "#6af7b820" },
  "subscription.created": { label: "Souscription", color: "var(--accent)",  bg: "#7c6af720" },
  "subscription.cancelled": { label: "Annulation",  color: "var(--danger)",  bg: "#f76a6a20" },
};

export default function AdminPage() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [tab, setTab]       = useState("dashboard");
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [txSearch, setTxSearch]     = useState("");
  const [editUser, setEditUser]     = useState(null);
  const [editForm, setEditForm]     = useState({});

  const { data: stats }   = useQuery({ queryKey: ["admin-stats"],   queryFn: () => adminAPI.stats().then(r => r.data), refetchInterval: 30000 });
  const { data: chart }   = useQuery({ queryKey: ["admin-chart"],   queryFn: () => adminAPI.revenueChart().then(r => r.data) });
  const { data: txData }  = useQuery({ queryKey: ["admin-tx", txSearch], queryFn: () => adminAPI.transactions({ search: txSearch, limit: 100 }).then(r => r.data) });
  const { data: usersData } = useQuery({ queryKey: ["admin-users", search, planFilter], queryFn: () => adminAPI.users({ search, plan: planFilter, limit: 100 }).then(r => r.data) });
  const { data: pendingMM, refetch: refetchMM } = useQuery({ queryKey: ["admin-mobilemoney"], queryFn: () => api.get("/mobilemoney/pending").then(r => r.data), refetchInterval: 30000 });

  const updateUser = useMutation({
    mutationFn: ({ id, data }) => adminAPI.updateUser(id, data),
    onSuccess: () => { qc.invalidateQueries(["admin-users"]); qc.invalidateQueries(["admin-stats"]); setEditUser(null); toast.success("Utilisateur mis a jour"); },
    onError: () => toast.error("Erreur"),
  });

  const s    = stats?.users || {};
  const tx   = stats?.transactions || {};
  const fmt  = n => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2 }).format(n || 0);
  const card = (extra) => ({ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, ...extra });
  const inp  = { background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px", color: "var(--text)", fontSize: 14, outline: "none", boxSizing: "border-box", width: "100%" };

  const openEdit = (u) => { setEditUser(u); setEditForm({ role: u.role, plan_id: u.plan_id, is_active: u.is_active, subscription_status: u.subscription_status || "inactive", subscription_ends_at: u.subscription_ends_at ? u.subscription_ends_at.split("T")[0] : "" }); };

  const TABS = [
    { key: "dashboard",    label: "Dashboard",      icon: "📊" },
    { key: "transactions", label: "Transactions",   icon: "💳" },
    { key: "users",        label: "Utilisateurs",   icon: "👥" },
    { key: "mobilemoney",  label: "Mobile Money",   icon: "📱" },
  ];

  const chartOpts = {
    responsive: true,
    plugins: { legend: { labels: { color: "#9090b0" } } },
    scales: { x: { grid: { color: "#2a2a3d" }, ticks: { color: "#9090b0" } }, y: { grid: { color: "#2a2a3d" }, ticks: { color: "#9090b0" } } },
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Syne,sans-serif", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>🛡️ Administration</h1>
          <p style={{ color: "var(--text2)", fontSize: 14 }}>Connecte en tant que <strong style={{ color: "var(--accent)" }}>{user?.name}</strong></p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ padding: "8px 16px", background: "#7c6af720", border: "1px solid #7c6af740", borderRadius: 20, fontSize: 13, color: "var(--accent)", fontWeight: 600 }}>⭐ Acces illimite</div>
          <div style={{ padding: "8px 16px", background: "#6af7b820", border: "1px solid #6af7b840", borderRadius: 20, fontSize: 13, color: "var(--accent3)", fontWeight: 600 }}>
            🔴 Live — {stats?.active_today || 0} actifs aujourd&apos;hui
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 4, marginBottom: 28, width: "fit-content" }}>
        {TABS.map(t_ => (
          <button key={t_.key} onClick={() => setTab(t_.key)}
            style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: tab === t_.key ? "var(--accent)" : "transparent", color: tab === t_.key ? "#fff" : "var(--text2)", fontSize: 14, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {t_.icon} {t_.label}
          </button>
        ))}
      </div>

      {/* ── Tab Dashboard ── */}
      {tab === "dashboard" && (
        <div>
          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 16, marginBottom: 24 }}>
            {[
              { label: "Utilisateurs",       value: s.total || 0,        color: "var(--accent)",  border: "#7c6af7", icon: "👥" },
              { label: "Premium actifs",      value: s.premium || 0,      color: "var(--accent2)", border: "#f7c26a", icon: "⭐" },
              { label: "Gratuits",            value: s.free_users || 0,   color: "var(--text2)",   border: "#555",    icon: "🆓" },
              { label: "Nouveaux (7j)",       value: s.new_7d || 0,       color: "var(--accent3)", border: "#6af7b8", icon: "🆕" },
              { label: "Inscrits aujourd'hui",value: stats?.new_today || 0, color: "var(--danger)", border: "#f76a6a", icon: "🔥" },
              { label: "Comptes familiaux",   value: s.family_accounts || 0, color: "#f76ab0",     border: "#f76ab0", icon: "👨‍👩‍👧" },
            ].map(k => (
              <div key={k.label} style={{ ...card({ borderTop: "3px solid " + k.border }) }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>{k.icon}</div>
                <div style={{ fontSize: 11, color: "var(--text2)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{k.label}</div>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Revenus */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
            {[
              { label: "Revenus totaux",    value: "$" + fmt(tx.total_revenue),  color: "var(--accent3)", icon: "💰" },
              { label: "Revenus (30j)",     value: "$" + fmt(tx.revenue_30d),    color: "var(--accent)",  icon: "📈" },
              { label: "Revenus (7j)",      value: "$" + fmt(tx.revenue_7d),     color: "var(--accent2)", icon: "📊" },
              { label: "MRR estimé",        value: "$" + fmt((s.premium || 0) * 2.99), color: "#f76ab0",  icon: "🔄" },
              { label: "ARR estimé",        value: "$" + fmt((s.premium || 0) * 2.99 * 12), color: "var(--danger)", icon: "📅" },
              { label: "Taux conversion",   value: s.total > 0 ? ((s.premium / s.total) * 100).toFixed(1) + "%" : "0%", color: "var(--accent3)", icon: "🎯" },
            ].map(k => (
              <div key={k.label} style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 12, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--text2)" }}>{k.label}</span>
                  <span>{k.icon}</span>
                </div>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 700, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Graphique revenus 12 mois */}
          {chart && chart.length > 0 && (
            <div style={{ ...card({ marginBottom: 24 }) }}>
              <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, marginBottom: 20 }}>Revenus — 12 derniers mois</h3>
              <Line data={{
                labels: chart.map(m => m.month),
                datasets: [
                  { label: "Revenus ($)", data: chart.map(m => m.revenue), borderColor: "#6af7b8", backgroundColor: "#6af7b820", tension: 0.4, fill: true, yAxisID: "y" },
                  { label: "Transactions", data: chart.map(m => m.transactions), borderColor: "#7c6af7", backgroundColor: "#7c6af720", tension: 0.4, fill: false, yAxisID: "y1" },
                ],
              }} options={{ ...chartOpts, scales: { ...chartOpts.scales, y: { ...chartOpts.scales.y, position: "left" }, y1: { ...chartOpts.scales.y, position: "right", grid: { drawOnChartArea: false } } } }} />
            </div>
          )}

          {/* Derniers inscrits */}
          <div style={card()}>
            <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, marginBottom: 16 }}>Derniers inscrits</h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Utilisateur", "Type", "Plan", "Email verifie", "Date inscription"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text3)", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {(usersData?.users || []).slice(0, 8).map(u => (
                    <tr key={u.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text2)" }}>{u.email}</div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: 12 }}>{u.account_type === "family" ? "👨‍👩‍👧 Familial" : "👤 Personnel"}</span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: u.plan_id === "premium" ? "#f7c26a20" : "var(--surface2)", color: u.plan_id === "premium" ? "#c98000" : "var(--text2)" }}>
                          {u.plan_id === "premium" ? "⭐ Premium" : "🆓 Gratuit"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: 12, color: u.email_verified ? "var(--accent3)" : "var(--danger)" }}>
                          {u.email_verified ? "✓ Verifie" : "✗ Non verifie"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text2)" }}>
                        {new Date(u.created_at).toLocaleDateString("fr-FR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Transactions ── */}
      {tab === "transactions" && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <input value={txSearch} onChange={e => setTxSearch(e.target.value)} placeholder="Rechercher par nom ou email..." style={{ ...inp, maxWidth: 400 }} />
          </div>
          <div style={card()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700 }}>
                Toutes les transactions
                <span style={{ marginLeft: 10, fontSize: 14, color: "var(--text2)", fontWeight: 400 }}>({txData?.total || 0})</span>
              </h3>
              <div style={{ fontSize: 14, color: "var(--accent3)", fontWeight: 600 }}>
                Total : ${fmt(txData?.transactions?.reduce((s, t) => s + parseFloat(t.amount || 0), 0))}
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Utilisateur", "Evenement", "Montant", "Methode", "Plan", "Reference", "Date"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text3)", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {(txData?.transactions || []).map(t => {
                    const ev = EVENT_LABELS[t.event_type] || { label: t.event_type, color: "var(--text2)", bg: "var(--surface2)" };
                    const method = t.payment_method || "paypal";
                    const pm = PAYMENT_METHODS[method] || PAYMENT_METHODS.paypal;
                    return (
                      <tr key={t.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "12px 12px" }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{t.user_name || "Inconnu"}</div>
                          <div style={{ fontSize: 12, color: "var(--text2)" }}>{t.user_email}</div>
                          <div style={{ fontSize: 11, color: "var(--text3)" }}>{t.account_type === "family" ? "👨‍👩‍👧" : "👤"} {t.account_type === "family" ? "Familial" : "Personnel"}</div>
                        </td>
                        <td style={{ padding: "12px 12px" }}>
                          <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: ev.bg, color: ev.color }}>
                            {ev.label}
                          </span>
                        </td>
                        <td style={{ padding: "12px 12px", fontFamily: "Syne,sans-serif", fontWeight: 700, color: "var(--accent3)", fontSize: 15 }}>
                          ${fmt(t.amount)}
                        </td>
                        <td style={{ padding: "12px 12px" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                            <span>{pm.icon}</span>
                            <span style={{ color: pm.color, fontWeight: 600 }}>{pm.label}</span>
                          </span>
                        </td>
                        <td style={{ padding: "12px 12px" }}>
                          <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, background: "#f7c26a20", color: "#c98000" }}>
                            {t.plan_id === "premium" ? "⭐ Premium" : t.plan_id}
                          </span>
                        </td>
                        <td style={{ padding: "12px 12px", fontSize: 12, color: "var(--text3)", fontFamily: "monospace" }}>
                          {t.stripe_event_id ? t.stripe_event_id.slice(0, 16) + "..." : "-"}
                        </td>
                        <td style={{ padding: "12px 12px", fontSize: 13, color: "var(--text2)", whiteSpace: "nowrap" }}>
                          {new Date(t.created_at).toLocaleDateString("fr-FR")}<br />
                          <span style={{ fontSize: 11, color: "var(--text3)" }}>{new Date(t.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {!(txData?.transactions?.length) && (
                    <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: "var(--text3)" }}>Aucune transaction</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Utilisateurs ── */}
      {tab === "users" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." style={{ ...inp, maxWidth: 320 }} />
            <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={{ ...inp, width: "auto" }}>
              <option value="">Tous les plans</option>
              <option value="premium">Premium</option>
              <option value="free">Gratuit</option>
            </select>
          </div>
          <div style={card()}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700 }}>Utilisateurs <span style={{ fontSize: 14, color: "var(--text2)", fontWeight: 400 }}>({usersData?.total || 0})</span></h3>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Utilisateur", "Type", "Plan", "Statut", "Activite", "Derniere connexion", "Actions"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text3)", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {(usersData?.users || []).map(u => (
                    <tr key={u.id} style={{ borderBottom: "1px solid var(--border)", opacity: u.is_active ? 1 : 0.5 }}>
                      <td style={{ padding: "12px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#7c6af7,#f7c26a)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                            {u.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name} {u.role === "admin" && <span style={{ fontSize: 11, color: "var(--accent)", background: "#7c6af720", padding: "2px 6px", borderRadius: 10 }}>Admin</span>}</div>
                            <div style={{ fontSize: 12, color: "var(--text2)" }}>{u.email}</div>
                            <div style={{ fontSize: 11, color: "var(--text3)" }}>{u.currency} · {u.language?.toUpperCase()}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px 12px", fontSize: 13 }}>
                        {u.account_type === "family" ? "👨‍👩‍👧 Familial" : "👤 Personnel"}
                      </td>
                      <td style={{ padding: "12px 12px" }}>
                        <div>
                          <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: u.plan_id === "premium" ? "#f7c26a20" : "var(--surface2)", color: u.plan_id === "premium" ? "#c98000" : "var(--text2)" }}>
                            {u.plan_id === "premium" ? "⭐ Premium" : "🆓 Gratuit"}
                          </span>
                          {u.subscription_ends_at && (
                            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>
                              Exp: {new Date(u.subscription_ends_at).toLocaleDateString("fr-FR")}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: "12px 12px" }}>
                        <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: u.is_active ? "#6af7b820" : "#f76a6a20", color: u.is_active ? "var(--accent3)" : "var(--danger)" }}>
                          {u.is_active ? "✓ Actif" : "✗ Suspendu"}
                        </span>
                        {!u.email_verified && <div style={{ fontSize: 10, color: "var(--danger)", marginTop: 2 }}>Email non verifie</div>}
                      </td>
                      <td style={{ padding: "12px 12px", fontSize: 12, color: "var(--text2)" }}>
                        <div>💸 {u.expense_count || 0} depenses</div>
                        <div>📈 {u.income_count || 0} revenus</div>
                        <div>💳 {u.payment_count || 0} paiements</div>
                      </td>
                      <td style={{ padding: "12px 12px", fontSize: 13, color: "var(--text2)" }}>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString("fr-FR") : <span style={{ color: "var(--text3)" }}>Jamais</span>}
                      </td>
                      <td style={{ padding: "12px 12px" }}>
                        <button onClick={() => openEdit(u)} style={{ padding: "6px 14px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                          Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Mobile Money ── */}
      {tab === "mobilemoney" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700 }}>
              Paiements Mobile Money en attente
              {(pendingMM?.filter(p => p.event_type === "payment.pending_manual").length || 0) > 0 && (
                <span style={{ marginLeft:10, background:"#f76a6a", color:"#fff", borderRadius:"50%", width:24, height:24, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700 }}>
                  {pendingMM.filter(p => p.event_type === "payment.pending_manual").length}
                </span>
              )}
            </h3>
          </div>
          <div style={{ ...card() }}>
            {!(pendingMM?.filter(p => p.event_type === "payment.pending_manual").length) ? (
              <div style={{ textAlign:"center", padding:60, color:"var(--text3)" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
                <p>Aucun paiement en attente</p>
              </div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>{["Utilisateur","Montant","Reference","Date","Actions"].map(h => (
                    <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", color:"var(--text3)", borderBottom:"1px solid var(--border)" }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {(pendingMM || []).filter(p => p.event_type === "payment.pending_manual").map(p => (
                      <tr key={p.id} style={{ borderBottom:"1px solid var(--border)" }}>
                        <td style={{ padding:"14px 12px" }}>
                          <div style={{ fontWeight:600, fontSize:14 }}>{p.user_name}</div>
                          <div style={{ fontSize:12, color:"var(--text2)" }}>{p.user_email}</div>
                        </td>
                        <td style={{ padding:"14px 12px", fontFamily:"Syne,sans-serif", fontWeight:700, color:"var(--accent3)", fontSize:16 }}>
                          ${parseFloat(p.amount).toFixed(2)}
                        </td>
                        <td style={{ padding:"14px 12px", fontFamily:"monospace", fontSize:13, color:"var(--text2)" }}>
                          {p.stripe_event_id}
                        </td>
                        <td style={{ padding:"14px 12px", fontSize:13, color:"var(--text2)" }}>
                          {new Date(p.created_at).toLocaleDateString("fr-FR")}<br/>
                          <span style={{ fontSize:11, color:"var(--text3)" }}>{new Date(p.created_at).toLocaleTimeString("fr-FR", { hour:"2-digit", minute:"2-digit" })}</span>
                        </td>
                        <td style={{ padding:"14px 12px" }}>
                          <div style={{ display:"flex", gap:8 }}>
                            <button onClick={async () => {
                              const interval = parseFloat(p.amount) > 10 ? (parseFloat(p.amount) > 50 ? "personal_yearly" : "family_monthly") : "personal_monthly";
                              try { await api.post("/mobilemoney/confirm/" + p.id, { interval }); toast.success("Premium active !"); refetchMM(); }
                              catch { toast.error("Erreur"); }
                            }} style={{ padding:"8px 16px", background:"var(--accent3)", color:"#0a2a1a", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                              ✓ Confirmer
                            </button>
                            <button onClick={async () => {
                              try { await api.post("/mobilemoney/reject/" + p.id); toast.success("Rejete"); refetchMM(); }
                              catch { toast.error("Erreur"); }
                            }} style={{ padding:"8px 16px", background:"#f76a6a20", border:"1px solid #f76a6a40", borderRadius:8, fontSize:13, color:"var(--danger)", cursor:"pointer" }}>
                              ✗ Rejeter
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal modifier utilisateur */}
      {editUser && (
        <div style={{ position: "fixed", inset: 0, background: "#00000090", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 20, padding: 32, width: "100%", maxWidth: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700 }}>Modifier — {editUser.name}</h3>
              <button onClick={() => setEditUser(null)} style={{ background: "none", border: "none", fontSize: 22, color: "var(--text2)", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ background: "var(--surface2)", borderRadius: 10, padding: 12, marginBottom: 20, fontSize: 13, color: "var(--text2)" }}>
              {editUser.email} · Inscrit le {new Date(editUser.created_at).toLocaleDateString("fr-FR")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>Role</label>
                <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} style={inp}>
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>Plan</label>
                <select value={editForm.plan_id} onChange={e => setEditForm({ ...editForm, plan_id: e.target.value })} style={inp}>
                  <option value="free">Gratuit</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>Statut abonnement</label>
                <select value={editForm.subscription_status} onChange={e => setEditForm({ ...editForm, subscription_status: e.target.value })} style={inp}>
                  <option value="inactive">Inactif</option>
                  <option value="active">Actif</option>
                  <option value="cancelled">Annule</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>Expiration</label>
                <input type="date" value={editForm.subscription_ends_at || ""} onChange={e => setEditForm({ ...editForm, subscription_ends_at: e.target.value })} style={inp} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>Compte</label>
              <select value={editForm.is_active ? "true" : "false"} onChange={e => setEditForm({ ...editForm, is_active: e.target.value === "true" })} style={inp}>
                <option value="true">Actif</option>
                <option value="false">Suspendu</option>
              </select>
            </div>
            {/* Actions rapides */}
            <div style={{ marginBottom: 20, padding: 16, background: "var(--surface2)", borderRadius: 12 }}>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 10, fontWeight: 600 }}>ACTIONS RAPIDES</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => setEditForm({ ...editForm, plan_id: "premium", subscription_status: "active", subscription_ends_at: new Date(Date.now() + 365*24*3600*1000).toISOString().split("T")[0] })}
                  style={{ padding: "6px 14px", background: "#f7c26a20", border: "1px solid #f7c26a40", borderRadius: 8, fontSize: 12, color: "#c98000", cursor: "pointer" }}>
                  ⭐ Premium 1 an
                </button>
                <button onClick={() => setEditForm({ ...editForm, plan_id: "premium", subscription_status: "active", subscription_ends_at: new Date(Date.now() + 30*24*3600*1000).toISOString().split("T")[0] })}
                  style={{ padding: "6px 14px", background: "#7c6af720", border: "1px solid #7c6af740", borderRadius: 8, fontSize: 12, color: "var(--accent)", cursor: "pointer" }}>
                  ⭐ Premium 1 mois
                </button>
                <button onClick={() => setEditForm({ ...editForm, plan_id: "free", subscription_status: "inactive", subscription_ends_at: "" })}
                  style={{ padding: "6px 14px", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--text2)", cursor: "pointer" }}>
                  Retirer Premium
                </button>
                <button onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })}
                  style={{ padding: "6px 14px", background: editForm.is_active ? "#f76a6a20" : "#6af7b820", border: "1px solid " + (editForm.is_active ? "#f76a6a40" : "#6af7b840"), borderRadius: 8, fontSize: 12, color: editForm.is_active ? "var(--danger)" : "var(--accent3)", cursor: "pointer" }}>
                  {editForm.is_active ? "Suspendre" : "Reactiver"}
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setEditUser(null)} style={{ flex: 1, padding: 12, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10, cursor: "pointer", color: "var(--text)" }}>Annuler</button>
              <button onClick={() => updateUser.mutate({ id: editUser.id, data: editForm })}
                style={{ flex: 1, padding: 12, background: "var(--accent)", color: "#fff", border: "none", borderRadius: 10, fontFamily: "Syne,sans-serif", fontWeight: 700, cursor: "pointer" }}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}